
import React, { useState, useEffect } from "react";
import { base44 } from "@/legacy/base44Client";
import { reportsService, commentsService, upvotesService } from "@/services";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  TrendingUp,
  MessageSquare,
  Send,
  AlertCircle,
  CheckCircle2,
  Clock,
  User as UserIcon,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const statusColors = {
  reported: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", icon: AlertCircle },
  acknowledged: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300", icon: Clock },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", icon: TrendingUp },
  waiting_contractor: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", icon: Clock },
  dependent_on: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", icon: Clock },
  resolved: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", icon: CheckCircle2 },
  closed: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300", icon: CheckCircle2 },
};

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  general_maintenance: "🛠️",
  other: "📍"
};

export default function ReportDetails() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const reportId = urlParams.get('id');
  const fromPage = urlParams.get('from'); // Track where we came from

  const [commentText, setCommentText] = useState("");
  const [hasUpvoted, setHasUpvoted] = useState(false);

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => reportsService.getById(reportId),
    enabled: !!reportId,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', reportId],
    queryFn: () => commentsService.filter({ report_id: reportId }, '-created_date'),
    enabled: !!reportId,
    initialData: [],
  });

  const { data: upvotes = [] } = useQuery({
    queryKey: ['upvotes', reportId],
    queryFn: () => upvotesService.filter({ report_id: reportId }),
    enabled: !!reportId,
    initialData: [],
  });

  useEffect(() => {
    if (user && upvotes.length > 0) {
      setHasUpvoted(upvotes.some(upvote => upvote.user_id === user.id));
    }
  }, [user, upvotes]);

  const addCommentMutation = useMutation({
    mutationFn: async (commentData) => {
      const comment = await commentsService.create(commentData);
      await reportsService.update(reportId, {
        comment_count: (report.comment_count || 0) + 1
      });
      return comment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setCommentText("");
      toast.success("💬 Comment posted successfully!", { duration: 2000 });
    },
    onError: () => {
      toast.error("Failed to post comment. Please try again.");
    },
  });

  const toggleUpvoteMutation = useMutation({
    mutationFn: async () => {
      if (hasUpvoted) {
        const myUpvote = upvotes.find(u => u.user_id === user.id);
        if (myUpvote) {
          await upvotesService.delete(myUpvote.id);
          await reportsService.update(reportId, {
            upvote_count: Math.max(0, (report.upvote_count || 0) - 1)
          });
        }
      } else {
        await upvotesService.create({
          report_id: reportId,
          user_id: user.id
        });
        await reportsService.update(reportId, {
          upvote_count: (report.upvote_count || 0) + 1
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upvotes', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      const newHasUpvoted = !hasUpvoted;
      setHasUpvoted(newHasUpvoted);
      toast.success(newHasUpvoted ? "👍 Upvoted! Your support matters." : "Upvote removed", {
        duration: 2000
      });
    },
    onError: () => {
      toast.error("Failed to update upvote. Please try again.");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus) => {
      const updateData = { status: newStatus };
      if (newStatus === 'resolved') {
        updateData.resolved_date = new Date().toISOString();
      }
      return await reportsService.update(reportId, updateData);
    },
    onSuccess: (_, newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      toast.success(`✅ Status updated to "${newStatus.replace('_', ' ')}"`, { duration: 3000 });
    },
    onError: () => {
      toast.error("Failed to update status. Please try again.");
    },
  });

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      toast.error("Please enter a comment before posting");
      return;
    }

    addCommentMutation.mutate({
      report_id: reportId,
      user_email: user.email,
      user_name: user.full_name || user.email,
      content: commentText.trim(),
    });
  };

  const canUpdateStatus = user && (user.user_role === 'city_worker' || user.user_role === 'contractor');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Report Not Found</h2>
          <Button onClick={() => navigate(createPageUrl(fromPage || "MapView"))}>
            Back
          </Button>
        </Card>
      </div>
    );
  }

  const statusStyle = statusColors[report.status] || statusColors.reported;
  const StatusIcon = statusStyle.icon || AlertCircle;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-6xl mx-auto pb-8">
        {/* Header */}
        <div className="flex items-start gap-3 md:gap-4 mb-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(createPageUrl(fromPage || "MapView"))}
            className="border-emerald-200 hover:bg-emerald-50 dark:border-emerald-700 dark:hover:bg-emerald-900/20 flex-shrink-0 mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Estate Issue Details
            </h1>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6 min-w-0">
            {/* Report Card */}
            <Card className="p-4 md:p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <div className="flex items-start gap-3 md:gap-4 mb-4">
                <span className="text-3xl md:text-4xl flex-shrink-0">{categoryIcons[report.category] || "📍"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 md:gap-4 mb-3">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">{report.title}</h2>
                    <Badge className={`${statusStyle.bg} ${statusStyle.text} whitespace-nowrap self-start sm:self-auto flex-shrink-0`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {report.status.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2 md:gap-3 text-xs md:text-sm text-gray-600 dark:text-gray-300 mb-4">
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                      <span>{format(new Date(report.created_date), 'MMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <UserIcon className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="truncate max-w-[150px]">{report.reporter_name}</span>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-gray-700 dark:text-gray-200 leading-relaxed mb-4">
                    {report.description}
                  </p>

                  {/* Photos */}
                  {report.photo_urls && report.photo_urls.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3 mb-4">
                      {report.photo_urls.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Report photo ${index + 1}`}
                          className="w-full h-32 md:h-40 object-cover rounded-lg border-2 border-emerald-100 dark:border-emerald-700 hover:border-emerald-300 dark:hover:border-emerald-500 transition-colors cursor-pointer"
                          onClick={() => window.open(url, '_blank')}
                        />
                      ))}
                    </div>
                  )}

                  {/* Engagement */}
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 pt-4 border-t dark:border-gray-700">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleUpvoteMutation.mutate()}
                      disabled={!user || toggleUpvoteMutation.isPending}
                      className={hasUpvoted ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700" : ""}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      {report.upvote_count || 0} Upvotes
                    </Button>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <MessageSquare className="w-4 h-4" />
                      {report.comment_count || 0} Comments
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Comments Section */}
            <Card className="p-4 md:p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                Comments ({comments.length})
              </h3>

              {/* Add Comment Form */}
              {user ? (
                <form onSubmit={handleCommentSubmit} className="mb-6">
                  <Textarea
                    placeholder="Share your thoughts or additional information..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={3}
                    className="mb-3"
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={!commentText.trim() || addCommentMutation.isPending}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Post Comment
                    </Button>
                  </div>
                </form>
              ) : (
                <Card className="p-4 mb-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-200 text-center">
                    Please <button onClick={() => navigate('/Login')} className="underline font-medium">login</button> to add comments
                  </p>
                </Card>
              )}

              {/* Comments List */}
              <div className="space-y-4">
                <AnimatePresence>
                  {comments.map((comment) => (
                    <motion.div
                      key={comment.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="flex gap-3 p-3 md:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <Avatar className="w-8 h-8 md:w-10 md:h-10 border-2 border-emerald-200 dark:border-emerald-700 flex-shrink-0">
                        <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300">
                          {comment.user_name?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{comment.user_name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {format(new Date(comment.created_date), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-200 text-sm break-words">{comment.content}</p>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {comments.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">No comments yet. Be the first to comment!</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 md:space-y-6 min-w-0">
            {/* Status Management */}
            {canUpdateStatus && (
              <Card className="p-4 md:p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold mb-4">Update Status</h3>
                <Select
                  value={report.status}
                  onValueChange={(value) => updateStatusMutation.mutate(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reported">Reported</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            )}

            {/* Report Info */}
            <Card className="p-4 md:p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <h3 className="font-bold mb-4">Issue Information</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Category:</span>
                  <p className="font-medium capitalize break-words">{report.category.replace('_', ' ')}</p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Priority:</span>
                  <p className="font-medium capitalize">{report.priority}</p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Reported by:</span>
                  <p className="font-medium break-words">{report.reporter_name}</p>
                </div>
                <div>
                  <p className="font-medium">{format(new Date(report.created_date), 'PPP')}</p>
                </div>
                {report.resolved_date && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Resolved:</span>
                    <p className="font-medium">{format(new Date(report.resolved_date), 'PPP')}</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
